// One time formatter for the whole app (SPEC D19, contract timezone rule):
// ISO *At fields are rendered in Europe/Zurich with an explicit CEST or CET
// label; *Hkt fields (Hong Kong strings from the commercial routes) are parsed
// back to instants first. Nothing here invents a time: invalid input is null.

export const ZURICH_TIME_ZONE = 'Europe/Zurich';
export const TIME_UNAVAILABLE = 'Unavailable';

export type ZurichLabel = 'CEST' | 'CET';

type TimeInput = string | number | Date | null | undefined;

const MONTHS: Readonly<Record<string, number>> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
};
const HKT_OFFSET_HOURS = 8;
const HKT_TEXT = /^(\d{1,2}) ([A-Za-z]{3,4}) (\d{4}),? (\d{2}):(\d{2})(?::(\d{2}))? HKT$/u;
const HKT_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?\+08:00$/u;

// "DD Mon YYYY, HH:MM:SS HKT" (research routes) or ISO with +08:00 (call
// tracker). Sentinels like "Observed time unavailable" return null.
export function parseHkt(value: string | null | undefined): Date | null {
  if (!value) return null;
  const text = value.trim();
  if (HKT_ISO.test(text)) {
    const date = new Date(text);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const match = HKT_TEXT.exec(text);
  if (!match) return null;
  const [, day, monthName, year, hour, minute, second] = match;
  const month = MONTHS[(monthName ?? '').toLowerCase()];
  if (month === undefined) return null;
  const utcMillis = Date.UTC(
    Number(year),
    month,
    Number(day),
    Number(hour) - HKT_OFFSET_HOURS,
    Number(minute),
    Number(second ?? '0'),
  );
  const date = new Date(utcMillis);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Accepts an ISO string, epoch millis, a Date, or an HKT string.
export function toDate(value: TimeInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return Number.isFinite(value) ? new Date(value) : null;
  const text = value.trim();
  if (text.endsWith('HKT') || HKT_ISO.test(text)) return parseHkt(text);
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function offsetHours(date: Date): number | null {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: ZURICH_TIME_ZONE, timeZoneName: 'longOffset' }).formatToParts(date);
  const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
  const match = /GMT([+-])(\d{1,2})(?::(\d{2}))?/u.exec(name);
  if (!match) return null;
  const sign = match[1] === '-' ? -1 : 1;
  return sign * (Number(match[2]) + Number(match[3] ?? '0') / 60);
}

// Summer time in Zurich is UTC+2 (CEST); winter is UTC+1 (CET).
export function zurichLabel(value: TimeInput): ZurichLabel | null {
  const date = toDate(value);
  if (!date) return null;
  const offset = offsetHours(date);
  if (offset === null) return null;
  return offset >= 2 ? 'CEST' : 'CET';
}

export interface ZurichFormatOptions {
  readonly date?: 'full' | 'dayMonth' | 'none';
  readonly seconds?: boolean;
  readonly label?: boolean;
}

function padded(value: number): string {
  return String(value).padStart(2, '0');
}

interface ZurichParts {
  readonly day: string;
  readonly month: string;
  readonly year: string;
  readonly hour: string;
  readonly minute: string;
  readonly second: string;
}

function zurichParts(date: Date): ZurichParts {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: ZURICH_TIME_ZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes): string => parts.find((part) => part.type === type)?.value ?? '';
  // Newer ICU builds print "24" for midnight with hourCycle h24; normalise to "00".
  const hour = read('hour') === '24' ? '00' : read('hour');
  return {
    day: read('day'),
    month: read('month').replace(/\.$/u, ''),
    year: read('year'),
    hour: hour || padded(0),
    minute: read('minute'),
    second: read('second'),
  };
}

// Default: "17 Aug 2026, 17:54:14 CEST". Options trim the date or seconds.
export function formatZurich(value: TimeInput, options: ZurichFormatOptions = {}): string | null {
  const date = toDate(value);
  if (!date) return null;
  const parts = zurichParts(date);
  const label = zurichLabel(date);
  const time = options.seconds === false ? `${parts.hour}:${parts.minute}` : `${parts.hour}:${parts.minute}:${parts.second}`;
  const dateMode = options.date ?? 'full';
  const datePart = dateMode === 'full'
    ? `${parts.day} ${parts.month} ${parts.year}, `
    : dateMode === 'dayMonth'
      ? `${parts.day} ${parts.month} `
      : '';
  const labelPart = options.label === false || label === null ? '' : ` ${label}`;
  return `${datePart}${time}${labelPart}`;
}

export function formatZurichDateTime(value: TimeInput): string | null {
  return formatZurich(value);
}

// "17:54:14 CEST" for status lines and capture records.
export function formatZurichTime(value: TimeInput): string | null {
  return formatZurich(value, { date: 'none' });
}

// "17:54 CEST" for the inspector clock lines.
export function formatZurichClock(value: TimeInput): string | null {
  return formatZurich(value, { date: 'none', seconds: false });
}

// "14 Aug 05:26 CEST" for group headers.
export function formatZurichDayTime(value: TimeInput): string | null {
  return formatZurich(value, { date: 'dayMonth', seconds: false });
}

// "17 Aug 2026" for date-only cells.
export function formatZurichDate(value: TimeInput): string | null {
  const date = toDate(value);
  if (!date) return null;
  const parts = zurichParts(date);
  return `${parts.day} ${parts.month} ${parts.year}`;
}

// Convenience for *Hkt fields: parse, then format in Zurich.
export function formatHktAsZurich(value: string | null | undefined, options: ZurichFormatOptions = {}): string | null {
  const date = parseHkt(value);
  return date ? formatZurich(date, options) : null;
}

const SECOND = 1_000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// Relative age with tabular units: "42 s", "5 m", "3 h 12 m", "3 d 13 h".
export function relativeAge(value: TimeInput, now: number = Date.now()): string | null {
  const date = toDate(value);
  if (!date) return null;
  const elapsed = Math.max(0, now - date.getTime());
  if (elapsed < MINUTE) return `${Math.floor(elapsed / SECOND)} s`;
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)} m`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)} h ${Math.floor((elapsed % HOUR) / MINUTE)} m`;
  return `${Math.floor(elapsed / DAY)} d ${Math.floor((elapsed % DAY) / HOUR)} h`;
}
